import { SANDBOX_REGISTRATION_URL } from './config';

const sandboxNetworkError = (target: string): Error =>
  new Error(`Network access is disabled in the local sandbox: ${target}`);

const YANDEX_METRIKA_HTTPS_HOSTS = new Set([
  'mc.webvisor.com',
  'mc.webvisor.org',
  'mc.yandex.ru',
  'yastatic.net',
]);

const YANDEX_METRIKA_WSS_HOSTS = new Set([
  'mc.webvisor.com',
  'mc.webvisor.org',
  'mc.yandex.ru',
]);

const toTarget = (input: string | URL | Request): string => {
  if (typeof input === 'string') return input;
  if (input instanceof Request) return input.url;
  return input.toString();
};

export const isYandexMetrikaUrl = (target: string | URL): boolean => {
  try {
    const url = new URL(target.toString(), window.location.href);
    if (url.protocol === 'https:') {
      return YANDEX_METRIKA_HTTPS_HOSTS.has(url.hostname);
    }
    if (url.protocol === 'wss:') {
      return YANDEX_METRIKA_WSS_HOSTS.has(url.hostname);
    }
    return false;
  } catch {
    return false;
  }
};

export const isLocalSandboxUrl = (
  target: string | URL,
  allowEmbeddedAssets = false
): boolean => {
  const rawTarget = target.toString();
  if (!rawTarget || rawTarget.startsWith('#')) return true;

  try {
    const url = new URL(rawTarget, window.location.href);
    if (allowEmbeddedAssets && ['blob:', 'data:'].includes(url.protocol)) {
      return true;
    }
    if (window.location.protocol === 'file:') return url.protocol === 'file:';
    return url.origin === window.location.origin;
  } catch {
    return false;
  }
};

export const isAllowedExternalNavigation = (target: string | URL): boolean => {
  try {
    const url = new URL(target.toString(), window.location.href);
    return url.href === SANDBOX_REGISTRATION_URL;
  } catch {
    return false;
  }
};

const assertLocalResource = (node: Node): void => {
  let target = '';

  if (node instanceof HTMLScriptElement) target = node.src;
  if (node instanceof HTMLIFrameElement) target = node.src;
  if (node instanceof HTMLMediaElement) target = node.currentSrc || node.src;
  if (node instanceof HTMLSourceElement) target = node.src;

  if (
    target &&
    !isLocalSandboxUrl(target, true) &&
    !isYandexMetrikaUrl(target)
  ) {
    throw sandboxNetworkError(target);
  }
};

export const installSandboxNetworkGuard = (): (() => void) => {
  const originalFetch = window.fetch;
  const originalOpenRequest = XMLHttpRequest.prototype.open;
  const originalSendBeacon = navigator.sendBeacon;
  const OriginalWebSocket = window.WebSocket;
  const originalWindowOpen = window.open;
  const OriginalAudio = window.Audio;
  const originalAppendChild = Node.prototype.appendChild;
  const originalInsertBefore = Node.prototype.insertBefore;

  window.fetch = (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> => {
    const target = toTarget(input);
    if (isYandexMetrikaUrl(target)) {
      return originalFetch.call(window, input, init);
    }
    return Promise.reject(sandboxNetworkError(target));
  };

  XMLHttpRequest.prototype.open = function sandboxOpen(
    this: XMLHttpRequest,
    method: string,
    url: string | URL,
    async?: boolean,
    username?: string | null,
    password?: string | null
  ): void {
    if (!isYandexMetrikaUrl(url)) {
      throw sandboxNetworkError(url.toString());
    }
    originalOpenRequest.call(
      this,
      method,
      url,
      async ?? true,
      username,
      password
    );
  } as typeof originalOpenRequest;

  navigator.sendBeacon = (url, data) =>
    isYandexMetrikaUrl(url)
      ? originalSendBeacon.call(navigator, url, data)
      : false;

  window.WebSocket = class SandboxWebSocket extends OriginalWebSocket {
    constructor(url: string | URL, protocols?: string | string[]) {
      if (!isYandexMetrikaUrl(url)) {
        throw sandboxNetworkError(url.toString());
      }
      super(url, protocols);
    }
  };

  window.open = ((url?: string | URL, ...args: unknown[]): Window | null => {
    if (url && !isLocalSandboxUrl(url) && !isAllowedExternalNavigation(url)) {
      return null;
    }
    return originalWindowOpen.call(window, url, ...(args as []));
  }) as typeof window.open;

  const GuardedAudio = function (this: HTMLAudioElement, src?: string) {
    if (src && !isLocalSandboxUrl(src, true)) {
      throw sandboxNetworkError(src);
    }
    return new OriginalAudio(src);
  } as unknown as typeof Audio;
  GuardedAudio.prototype = OriginalAudio.prototype;
  window.Audio = GuardedAudio;

  Node.prototype.appendChild = function sandboxAppendChild<T extends Node>(
    newChild: T
  ): T {
    assertLocalResource(newChild);
    return originalAppendChild.call(this, newChild) as T;
  };

  Node.prototype.insertBefore = function sandboxInsertBefore<T extends Node>(
    newChild: T,
    refChild: Node | null
  ): T {
    assertLocalResource(newChild);
    return originalInsertBefore.call(this, newChild, refChild) as T;
  };

  const blockExternalAnchor = (event: MouseEvent): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const anchor = target.closest('a[href]');
    if (!(anchor instanceof HTMLAnchorElement)) return;
    if (
      isLocalSandboxUrl(anchor.href) ||
      isAllowedExternalNavigation(anchor.href)
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
  };
  document.addEventListener('click', blockExternalAnchor, true);

  return () => {
    window.fetch = originalFetch;
    XMLHttpRequest.prototype.open = originalOpenRequest;
    navigator.sendBeacon = originalSendBeacon;
    window.WebSocket = OriginalWebSocket;
    window.open = originalWindowOpen;
    window.Audio = OriginalAudio;
    Node.prototype.appendChild = originalAppendChild;
    Node.prototype.insertBefore = originalInsertBefore;
    document.removeEventListener('click', blockExternalAnchor, true);
  };
};
